"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import Link from "next/link";
import { useState } from "react";
import { CaretDown } from "@/app/components/Icons/Phosphoricons";

const faqs = [
    {
        id: 1,
        question: "How do you keep costs affordable for small businesses?",
        answer:
            "We leverage a proprietary library of reusable components and tools, allowing us to build high quality solutions efficiently. This reduces development time and eliminates the need for costly third party tools, keeping prices low without compromising quality.",
    },
    {
        id: 2,
        question: "Is my data stored overseas?",
        answer:
            "No, all data is securely stored on US based servers, ensuring compliance with US regulations and protecting your business from foreign data risks.",
    },
    {
        id: 3,
        question: "Is everything made in America?",
        answer:
            "Yes! As a veteran owned company, we take pride in ensuring that all work is done in the United States.",
    },
    {
        id: 4,
        question: "Do you outsource work overseas?",
        answer:
            "No, we do not outsource any work overseas. Keeping everything in house ensures maximum security, quality control, and compliance with US standards.",
    },
    {
        id: 5,
        question: "How do you prevent bugs in your software?",
        answer:
            "We implement rigorous testing protocols, automated test coverage, and continuous updates to identify and fix potential issues early. Our proactive approach ensures reliable, secure software solutions.",
    },
    {
        id: 6,
        question: "Is AWS a good cloud service?",
        answer:
            "Absolutely! AWS is an enterprise grade cloud platform offering unmatched security, scalability, and cost effectiveness. It remains the industry leader in cloud infrastructure.",
    },
    {
        id: 7,
        question: "What platforms do you develop apps for?",
        answer:
            "We develop Web Apps, Mobile Apps, Desktop Apps, and Cross Platform Apps. Whether it's Windows, macOS, Linux, iOS, or Android, we specialize in creating custom software solutions tailored to your unique business needs.",
    },
    {
        id: 8,
        question: "Do you provide 24/7 monitoring and alerts for system issues?",
        answer:
            "Yes, we offer 24/7 monitoring and real time alerts for system issues. Our dedicated team ensures proactive monitoring and rapid response to keep your business running smoothly.",
    },
    {
        id: 9,
        question: "Do you develop eCommerce websites and other types of apps?",
        answer:
            "Yes! We have extensive experience building a wide range of websites and applications, including eCommerce platforms, social networks, enterprise solutions, and more.",
    },
    {
        id: 10,
        question: "How do you ensure data security and privacy?",
        answer:
            "We implement industry leading security measures, including encryption, access controls, and regular security audits, to protect your data and ensure compliance with data protection regulations.",
    },
    {
        id: 11,
        question: "Can you integrate with third party APIs and services?",
        answer:
            "Yes, we have experience integrating with a wide range of third party APIs and services, including payment gateways, social media platforms, and other business tools.",
    },
    {
        id: 12,
        question: "Do you provide ongoing support and maintenance?",
        answer:
            "Yes, we offer ongoing support and maintenance services to ensure your software remains up to date, secure, and optimized for performance.",
    },
    {
        id: 13,
        question: "What sets you apart from other software development companies?",
        answer:
            "Our commitment to quality, security, and customer satisfaction sets us apart. We prioritize transparency, communication, and collaboration to deliver exceptional software solutions tailored to your unique business needs.",
    },
    {
        id: 14,
        question: "How do you handle scalability and growth?",
        answer:
            "We design scalable software architectures that can grow with your business. Our solutions are built to handle increased demand, new features, and evolving technology trends, ensuring long term success.",
    },
    {
        id: 15,
        question: "Do you offer custom pricing?",
        answer:
            "Yes, we provide custom pricing based on your specific requirements and project scope. Our goal is to deliver cost effective solutions that meet your needs and budget.",
    },
]

export default function FrequentlyAskedQuestions() {
    const pathname = usePathname();
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    // Toggle FAQ open/closed state
    const toggleFaq = (id: number) => {
        setOpenFaq(openFaq === id ? null : id);
    };

    // Generate structured FAQ schema for SEO
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
            },
        })),
    };

    return (
        <>
            {/* SEO JSON-LD Structured Data */}
            <Script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

            <section className="bg-gradient-to-b from-blue-50 to-white py-24 sm:py-32" aria-labelledby="faq-heading">
                <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8">
                    {/* Header Section with visual enhancement */}
                    <header className="mx-auto max-w-2xl text-center">
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-4 py-1 text-sm font-medium text-blue-800 mb-6">
                            Get Answers
                        </span>
                        <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                            Frequently Asked Questions
                        </h2>
                        <div className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">
                            <p>
                                Have a different question and can't find the answer you're looking for?{" "}
                                {pathname === "/contact" ? (
                                    "Contact us"
                                ) : (
                                    <Link href="/contact" className="font-semibold text-blue-600 hover:text-blue-500 transition-colors">
                                        Contact us
                                    </Link>
                                )}
                                {" "} and we'll get back to you as soon as we can.
                            </p>
                        </div>
                    </header>

                    {/* Enhanced FAQ Accordion */}
                    <div className="mt-12 max-w-4xl mx-auto">
                        <div className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white shadow-sm">
                            {faqs.map((faq) => (
                                <div key={faq.id} className="overflow-hidden">
                                    <button
                                        onClick={() => toggleFaq(faq.id)}
                                        className="flex w-full items-center justify-between px-6 py-5 text-left transition focus:outline-none focus:ring-2 focus:ring-offset-2"
                                        aria-expanded={openFaq === faq.id}
                                    >
                                        <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                                        <CaretDown
                                            className={`h-5 w-5 text-blue-600 transform transition-transform duration-200 ${openFaq === faq.id ? 'rotate-180' : ''}`}
                                            weight="bold"
                                        />
                                    </button>
                                    <div
                                        className={`px-6 pb-6 ${openFaq === faq.id ? 'block' : 'hidden'}`}
                                    >
                                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Additional Help Section */}
                    <div className="mt-16 text-center">
                        <div className="mx-auto max-w-3xl rounded-2xl bg-blue-50 p-8 sm:p-10">
                            <h3 className="text-xl font-semibold text-gray-900">Still have questions?</h3>
                            <p className="mt-4 text-gray-600">
                                We're here to help. Our team is ready to answer any questions you might have about our services.
                            </p>
                            <div className="mt-6">
                                <Link
                                    href="/contact"
                                    className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                >
                                    Contact Our Team
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}